import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { justForSideEffect, justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import { Movement, Product } from '../types'
import {
  Barcode,
  EditBarcode,
  FlvProductUpdateAvailabilityPayload,
  GetFlvProductInfoForReleasePayload,
  GetInvoiceToPrintPayload,
  GetProduct,
  InsertBarcodeParams,
  MovementByType,
  ProductTabsFilters,
  Reserve,
  SetProperty,
  StockManagementState,
  UpOrDownOrderBarcode,
} from './StockManagementTypes'
import { cloneDeep } from 'lodash'

const initialState: StockManagementState = {
  productTabs: {
    filters: {},
    barcodes: [],
    reserves: {
      items: [],
      totalItems: 0,
    },
    movements: {
      items: [],
      totalItems: 0,
      page: 1,
    },
    movementsByType: [],
  },
  product: null,
  flvRuleDayPeriod: undefined,
  flvProductInfoForRelease: [],
  selectedFlvProducts: [],
}

const getProductReserves = justForSideEffect()

const getProductBarcodes = justForSideEffect()

const getProductMovements = justForSideEffect()

const getProductMovementsByType = justForSideEffect()

const getProduct = justForSideEffectWithPayload<GetProduct>()

const deleteBarcode = justForSideEffectWithPayload<EditBarcode>()

const insertNewBarcode = justForSideEffectWithPayload<InsertBarcodeParams>()

const upBarcodeOrder = justForSideEffectWithPayload<UpOrDownOrderBarcode>()

const downBarcodeOrder = justForSideEffectWithPayload<UpOrDownOrderBarcode>()

const getFlvProductInfoForRelease = justForSideEffectWithPayload<GetFlvProductInfoForReleasePayload>()

const resendUpdateForFlvRelease = justForSideEffectWithPayload<FlvProductUpdateAvailabilityPayload>()

const getInvoiceToPrint = justForSideEffectWithPayload<GetInvoiceToPrintPayload>()

const clearProductTabs = (state: StockManagementState) => ({
  ...state,
  productTabs: initialState.productTabs,
})

const clearMovementsTab = (state: StockManagementState) => ({
  ...state,
  productTabs: {
    ...state.productTabs,
    movements: initialState.productTabs.movements,
  },
})

const clearMovementsByTypeTab = (state: StockManagementState): StockManagementState => ({
  ...state,
  productTabs: {
    ...state.productTabs,
    movementsByType: initialState.productTabs.movementsByType,
  },
})

const clearProductReserves = (
  state: StockManagementState,
) => (
  {
    ...state,
    productTabs: {
      ...state.productTabs,
      reserves: initialState.productTabs.reserves,
    },
  }
)

const setProduct = (state: StockManagementState, action: PayloadAction<Product| null>) => ({
  ...state,
  product: action.payload,
})

const setProductBarcodes = (state: StockManagementState, action: PayloadAction<Barcode[]>) => ({
  ...state,
  productTabs: { ...state.productTabs, barcodes: action.payload },
})

const setProductMovements = (
  state: StockManagementState,
  action: PayloadAction<{ items: Movement[], totalItems: number }>,
) => ({
  ...state,
  productTabs: {
    ...state.productTabs,
    movements: {
      ...state.productTabs.movements,
      items: [...state.productTabs.movements.items, ...action.payload.items],
      totalItems: action.payload.totalItems,
    },
  },
})

const setProductMovementsByType = (
  state: StockManagementState,
  action: PayloadAction<{ items: MovementByType[]}>,
) => ({
  ...state,
  productTabs: {
    ...state.productTabs,
    movementsByType: [...state.productTabs.movementsByType, ...action.payload.items],
  },
})

const setProductReserves = (
  state: StockManagementState,
  action: PayloadAction<{items: Reserve[], totalItems: number}>,
) => (
  {
    ...state,
    productTabs: {
      ...state.productTabs,
      reserves: {
        ...state.productTabs.reserves,
        items: [...state.productTabs.reserves.items, ...action.payload.items],
        totalItems: action.payload.totalItems,
      },
    },
  }
)

const nextProductMovementsPage = (state: StockManagementState) => ({
  ...state,
  productTabs: {
    ...state.productTabs,
    movements: {
      ...state.productTabs.movements,
      page: state.productTabs.movements.page + 1,
    },
  },
})

const setProductTabsFilters = (
  state: StockManagementState,
  action: PayloadAction<ProductTabsFilters>,
) => ({
  ...state,
  productTabs: { ...state.productTabs, filters: action.payload },
})

const addBarcode = (
  state: StockManagementState,
  { payload }: PayloadAction<Barcode>,
): StockManagementState => {
  const barcodes = [...state.productTabs.barcodes, payload]

  return {
    ...state,
    productTabs: {
      ...state.productTabs,
      barcodes,
    },
  }
}

const setProperty = (state: StockManagementState, action: PayloadAction<SetProperty>): StockManagementState => {
  const { property, value } = action.payload

  return {
    ...state,
    [property]: value,
  }
}

const clearProperty = (
  state: StockManagementState,
  action: PayloadAction<string | string[]>,
): StockManagementState => {
  const newState = cloneDeep(state)

  if (Array.isArray(action.payload)) {
    action.payload.forEach((property) => {
      newState[property] = initialState[property]
    })
  } else {
    newState[action.payload] = initialState[action.payload]
  }

  return newState
}

const slice = createSlice({
  name: 'stockManagement',
  initialState,
  reducers: {
    clearProductTabs,
    clearMovementsTab,
    clearMovementsByTypeTab,
    clearProductReserves,
    getProduct,
    getProductBarcodes,
    getProductMovements,
    getProductMovementsByType,
    getProductReserves,
    nextProductMovementsPage,
    setProduct,
    setProductBarcodes,
    setProductMovements,
    setProductMovementsByType,
    setProductReserves,
    setProductTabsFilters,
    deleteBarcode,
    insertNewBarcode,
    addBarcode,
    upBarcodeOrder,
    downBarcodeOrder,
    getFlvProductInfoForRelease,
    setProperty,
    clearProperty,
    resendUpdateForFlvRelease,
    getInvoiceToPrint,
  },
})

export default slice.reducer

export const { actions } = slice
